'use client';

import AiSuggestionChip from '@carvana/ds-ai-suggestion-chip';
import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import { useTrackEvent } from '~/context/common/AnalyticsProvider';
import useComposerStarterQuestionRotation, {
  SLIDE_MS
} from '~/hooks/home/useComposerStarterQuestionRotation';
import styles from './HomeStarterQuestion.module.css';

const TYPING_CURSOR = '▏';

type OwnProps = {
  disabled?: boolean;
  onSelectAction: (question: string) => void;
  questions: ReadonlyArray<string>;
};

export default function HomeStarterQuestion({
  disabled = false,
  onSelectAction,
  questions
}: OwnProps) {
  const { currentQuestion, displayedText, previousQuestion, sliding, typing } =
    useComposerStarterQuestionRotation(questions, !disabled);
  const hasPeek = questions.length > 1;
  const pinnedQuestionRef = useRef<string | null>(null);
  const trackEvent = useTrackEvent();
  const trackRef = useRef<HTMLDivElement>(null);
  const [restingOffset, setRestingOffset] = useState<number | null>(null);

  // Height/offset are measured from the rendered chip, not hardcoded, so a DS
  // release can't silently break them. Applied as plain inline styles rather
  // than CSS custom properties + calc() — a real device showed no tip peek at
  // all with that approach, consistent with the custom-property update not
  // landing before calc() read it.
  useLayoutEffect(() => {
    const track = trackRef.current;
    const viewport = track?.parentElement;
    const chipSlot = viewport?.parentElement;
    if (!track || !viewport || !chipSlot) return undefined;

    const children = Array.from(track.children) as Array<HTMLElement>;
    const current = hasPeek ? children[1] : children[0];
    if (!current) return undefined;

    const verticalPadding =
      parseFloat(getComputedStyle(viewport).paddingTop) * 2;

    const measure = () => {
      const chipHeight = current.offsetHeight;
      const tipPeek = hasPeek ? Math.round(chipHeight * 0.5) : 0;

      // Chip height only, no padding — chipSlot isn't centered around the
      // chip, so extra height here would sit below it and skew alignment.
      chipSlot.style.height = `${chipHeight}px`;
      chipSlot.style.width = `${current.offsetWidth}px`;
      viewport.style.height = `${chipHeight + tipPeek + verticalPadding}px`;

      if (hasPeek && children[2]) {
        setRestingOffset(children[2].offsetTop - children[1].offsetTop);
      }
    };

    measure();

    const observer = new ResizeObserver(measure);
    children.forEach(child => observer.observe(child));

    return () => observer.disconnect();
  }, [currentQuestion, previousQuestion, hasPeek]);

  const handlePointerDown = useCallback(() => {
    pinnedQuestionRef.current = currentQuestion;
  }, [currentQuestion]);

  const handleClick = useCallback(() => {
    if (disabled) return;

    const question = pinnedQuestionRef.current ?? currentQuestion;
    pinnedQuestionRef.current = null;

    trackEvent('composer_shell_questions_click', {
      action: 'clicked',
      question
    });

    onSelectAction(question);
  }, [currentQuestion, disabled, onSelectAction, trackEvent]);

  return (
    <div
      className="flex shrink-0 items-center gap-sm"
      data-testid="home-starter-question"
    >
      <span className="text-text-strong t-label-sm">Get started</span>
      <div className={styles.chipSlot}>
        <div className={styles.viewport}>
          <div
            className={styles.track}
            ref={trackRef}
            style={
              hasPeek && restingOffset !== null
                ? {
                    transform: `translateY(${sliding ? 0 : -restingOffset}px)`,
                    transition: sliding
                      ? `transform ${SLIDE_MS}ms ease-in-out`
                      : undefined
                  }
                : undefined
            }
          >
            {hasPeek && (
              // Blank, not nextQuestion's full text — it's about to type from
              // empty the instant it lands, so showing it complete here just to
              // blank-and-retype would read as a glitch.
              <AiSuggestionChip
                aria-hidden
                data-analytics-id={null}
                iconLeft={false}
                label={' '}
                tabIndex={-1}
              />
            )}
            <AiSuggestionChip
              aria-busy={disabled}
              aria-label={currentQuestion}
              // trackEvent below already fires on click; a real analytics id
              // here would double-count the click via auto-instrumentation.
              data-analytics-id={null}
              data-testid="home-starter-question-chip"
              iconLeft={false}
              label={
                typing ? `${displayedText}${TYPING_CURSOR}` : displayedText
              }
              onClick={handleClick}
              onPointerDown={handlePointerDown}
              testId="home-starter-question-chip"
            />
            {hasPeek && (
              <AiSuggestionChip
                aria-hidden
                data-analytics-id={null}
                iconLeft={false}
                label={previousQuestion}
                tabIndex={-1}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
